import React, { createContext, useContext, useState, useEffect } from "react";
import {
  doc,
  setDoc,
  onSnapshot,
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  getDocs,
  getDoc,
  where,
  deleteDoc,
} from "firebase/firestore";
import { db, storage } from "../firebase/config";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import {
  MISSION_SLOT_COUNT,
  createEmptyMission,
  createDefaultGameProgress,
  MISSION_TYPES,
} from "../constants/missions";
import {
  applyGameEvent,
  findCompletableMissions,
  getMissionProgressView,
} from "../services/gameProgress";
import { syncOpsTicklesDisplayName } from "../services/opsTicklesScores";
import {
  PIXEL_AVATAR_STORAGE_KEY,
  deserializePixelAvatar,
  serializePixelAvatar,
} from "../utils/pixelAvatar";

const FirebaseContext = createContext();

// Safe localStorage accessors to avoid Node build-time issues
const hasLocalStorage = () => {
  try {
    return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
  } catch {
    return false;
  }
};
const safeGetItem = (key) => {
  try {
    if (!hasLocalStorage()) return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const safeSetItem = (key, value) => {
  try {
    if (!hasLocalStorage()) return;
    window.localStorage.setItem(key, value);
  } catch {}
};
const safeRemoveItem = (key) => {
  try {
    if (!hasLocalStorage()) return;
    window.localStorage.removeItem(key);
  } catch {}
};

const parseStoredPixelAvatar = (value) => {
  const decoded = deserializePixelAvatar(value);
  if (!decoded) return null;
  return serializePixelAvatar(decoded);
};

const loadLocalPixelAvatar = () => {
  try {
    const raw = safeGetItem(PIXEL_AVATAR_STORAGE_KEY);
    if (!raw) return null;
    return parseStoredPixelAvatar(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error("useFirebase must be used within a FirebaseProvider");
  }
  return context;
};

export const FirebaseProvider = ({ children }) => {
  const [userId, setUserId] = useState(null);
  const [points, setPoints] = useState(0);
  const [displayName, setDisplayName] = useState(null);
  const [language, setLanguage] = useState("en");
  const [loading, setLoading] = useState(true);
  const [gameProgress, setGameProgress] = useState(createDefaultGameProgress);
  const [missionProgress, setMissionProgress] = useState({});
  const [englishProgress, setEnglishProgress] = useState({
    learnerName: "",
    lessons: {},
  });
  const [pixelAvatar, setPixelAvatar] = useState(null);
  const [recentMissionClaims, setRecentMissionClaims] = useState([]);
  const pointsRef = React.useRef(0);
  const gameProgressRef = React.useRef(createDefaultGameProgress());
  const missionProgressRef = React.useRef({});
  const englishProgressRef = React.useRef({ learnerName: "", lessons: {} });

  // Use production_* collections when built for prod, or when explicitly requested.
  // Set REACT_APP_FIREBASE_ENV=production in client/.env.local to hit prod data locally.
  const isProduction =
    process.env.REACT_APP_FIREBASE_ENV === "production" ||
    (process.env.REACT_APP_FIREBASE_ENV !== "development" &&
      process.env.NODE_ENV === "production");
  const USERS_COLLECTION =
    process.env.REACT_APP_FIREBASE_USERS_COLLECTION ||
    (isProduction ? "production_users" : "users");
  const HIGHSCORES_COLLECTION =
    process.env.REACT_APP_FIREBASE_HIGHSCORES_COLLECTION ||
    (isProduction ? "production_dinoHighScores" : "dinoHighScores");
  const MAZE_HIGHSCORES_COLLECTION =
    process.env.REACT_APP_FIREBASE_MAZE_HIGHSCORES_COLLECTION ||
    (isProduction ? "production_mazeHighScores" : "mazeHighScores");
  const SOLITAIRE_HIGHSCORES_COLLECTION =
    process.env.REACT_APP_FIREBASE_SOLITAIRE_HIGHSCORES_COLLECTION ||
    (isProduction ? "production_solitaireHighScores" : "solitaireHighScores");
  const REWARD_CLAIMS_COLLECTION =
    process.env.REACT_APP_FIREBASE_REWARD_CLAIMS_COLLECTION ||
    (isProduction ? "production_rewardClaims" : "rewardClaims");
  const MISSIONS_COLLECTION =
    process.env.REACT_APP_FIREBASE_MISSIONS_COLLECTION ||
    (isProduction ? "production_missions" : "missions");
  const PROBLEM_REPORTS_COLLECTION =
    process.env.REACT_APP_FIREBASE_PROBLEM_REPORTS_COLLECTION ||
    (isProduction ? "production_problemReports" : "problemReports");

  // Admin analytics always use production collections — never the localhost/dev set.
  const ADMIN_USERS_COLLECTION = "production_users";
  const ADMIN_REWARD_CLAIMS_COLLECTION = "production_rewardClaims";
  const ADMIN_PROBLEM_REPORTS_COLLECTION = "production_problemReports";
  const ADMIN_DINO_HIGHSCORES_COLLECTION = "production_dinoHighScores";
  const ADMIN_MAZE_HIGHSCORES_COLLECTION = "production_mazeHighScores";
  const ADMIN_SOLITAIRE_HIGHSCORES_COLLECTION = "production_solitaireHighScores";
  const ADMIN_OPS_TICKLES_HIGHSCORES_COLLECTION =
    "production_opsTicklesPointsHighScores";
  const MAX_PROBLEM_REPORT_LENGTH = 2000;

  // Get or create user ID from localStorage
  useEffect(() => {
    let userId = safeGetItem("arcadeUserId");
    if (!userId) {
      userId = `user_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 11)}`;
      safeSetItem("arcadeUserId", userId);
    }
    setUserId(userId);
  }, []);

  useEffect(() => {
    pointsRef.current = points;
  }, [points]);

  useEffect(() => {
    gameProgressRef.current = gameProgress;
  }, [gameProgress]);

  useEffect(() => {
    missionProgressRef.current = missionProgress;
  }, [missionProgress]);

  useEffect(() => {
    englishProgressRef.current = englishProgress;
  }, [englishProgress]);

  // Load points from Firestore when userId is available
  useEffect(() => {
    if (!userId) return;

    // Check if Firebase is available
    if (!db) {
      // Fallback to localStorage only
      const savedPoints = safeGetItem("arcadePoints");
      if (savedPoints) {
        setPoints(parseInt(savedPoints, 10) || 0);
      }
      const savedLanguage = safeGetItem("arcadeLanguage");
      if (savedLanguage) {
        setLanguage(savedLanguage);
      }
      try {
        const savedProgress = safeGetItem("arcadeGameProgress");
        if (savedProgress) {
          const parsed = JSON.parse(savedProgress);
          setGameProgress({ ...createDefaultGameProgress(), ...parsed });
        }
        const savedMissions = safeGetItem("arcadeMissionProgress");
        if (savedMissions) {
          setMissionProgress(JSON.parse(savedMissions) || {});
        }
        const savedEnglish = safeGetItem("arcadeEnglishProgress");
        if (savedEnglish) {
          const parsed = JSON.parse(savedEnglish);
          setEnglishProgress({
            learnerName: String(parsed?.learnerName || "").trim(),
            lessons: { ...(parsed?.lessons || {}) },
          });
        }
        setPixelAvatar(loadLocalPixelAvatar());
      } catch {
        // ignore
      }
      setLoading(false);
      return;
    }

    const userDocRef = doc(db, USERS_COLLECTION, userId);

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setPoints(data.points || 0);
          setDisplayName(data.displayName || null);
          setLanguage(data.language || "en");
          setGameProgress({
            ...createDefaultGameProgress(),
            ...(data.gameProgress || {}),
            gamesPlayed: {
              ...createDefaultGameProgress().gamesPlayed,
              ...(data.gameProgress?.gamesPlayed || {}),
            },
          });
          setMissionProgress(data.missionProgress || {});
          setEnglishProgress({
            learnerName: String(
              (data.englishProgress && data.englishProgress.learnerName) || ""
            ).trim(),
            lessons: {
              ...((data.englishProgress && data.englishProgress.lessons) || {}),
            },
          });
          setPixelAvatar(
            parseStoredPixelAvatar(data.pixelAvatar) || loadLocalPixelAvatar()
          );
        } else {
          // Create user document if it doesn't exist
          const savedLanguage = safeGetItem("arcadeLanguage") || "en";
          setDoc(userDocRef, {
            points: 0,
            language: savedLanguage,
            gameProgress: createDefaultGameProgress(),
            missionProgress: {},
            englishProgress: { learnerName: "", lessons: {} },
            createdAt: new Date(),
          });
          setPoints(0);
          setDisplayName(null);
          setLanguage(savedLanguage);
          setGameProgress(createDefaultGameProgress());
          setMissionProgress({});
          setEnglishProgress({ learnerName: "", lessons: {} });
          setPixelAvatar(loadLocalPixelAvatar());
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error loading user data:", error);
        // Fallback to localStorage if Firestore fails
        const savedPoints = safeGetItem("arcadePoints");
        if (savedPoints) {
          setPoints(parseInt(savedPoints, 10) || 0);
        }
        const savedLanguage = safeGetItem("arcadeLanguage");
        if (savedLanguage) {
          setLanguage(savedLanguage);
        }
        const savedDisplayName = safeGetItem("arcadeDisplayName");
        if (savedDisplayName) {
          setDisplayName(savedDisplayName);
        }
        setPixelAvatar(loadLocalPixelAvatar());
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, USERS_COLLECTION]);

  // Update user data in Firestore
  const updateUserData = async (updates) => {
    if (!userId) return;

    // Always save to localStorage as backup
    if (updates.points !== undefined) {
      safeSetItem("arcadePoints", updates.points.toString());
    }
    if (updates.language !== undefined) {
      safeSetItem("arcadeLanguage", updates.language);
    }
    if (updates.displayName !== undefined) {
      if (updates.displayName) {
        safeSetItem("arcadeDisplayName", updates.displayName);
      } else {
        safeRemoveItem("arcadeDisplayName");
      }
    }
    if (updates.englishProgress !== undefined) {
      safeSetItem(
        "arcadeEnglishProgress",
        JSON.stringify(updates.englishProgress)
      );
    }
    if (updates.pixelAvatar !== undefined) {
      if (updates.pixelAvatar) {
        safeSetItem(
          PIXEL_AVATAR_STORAGE_KEY,
          JSON.stringify(updates.pixelAvatar)
        );
      } else {
        safeRemoveItem(PIXEL_AVATAR_STORAGE_KEY);
      }
    }

    // Try to save to Firestore if available
    if (!db) {
      return; // Firebase not configured, localStorage only
    }

    const userDocRef = doc(db, USERS_COLLECTION, userId);
    try {
      await setDoc(
        userDocRef,
        { ...updates, lastUpdated: new Date() },
        { merge: true }
      );
    } catch (error) {
      console.error("Error updating user data in Firestore:", error);
      // Data already saved to localStorage, so we're good
    }
  };

  // Update points in Firestore
  const updatePoints = async (newPoints) => {
    await updateUserData({ points: newPoints });
  };

  // Add points (delta can be positive or negative)
  const addPoints = async (delta) => {
    const newPoints = Math.max(0, pointsRef.current + delta);
    pointsRef.current = newPoints;
    setPoints(newPoints);
    await updatePoints(newPoints);
  };

  // Set points directly
  const setPointsDirect = async (newPoints) => {
    const pointsValue = Math.max(0, newPoints);
    pointsRef.current = pointsValue;
    setPoints(pointsValue);
    await updatePoints(pointsValue);
  };

  const syncHighScoresDisplayName = async (newDisplayName) => {
    if (!db || !userId) return;
    const collections = [
      HIGHSCORES_COLLECTION,
      MAZE_HIGHSCORES_COLLECTION,
      SOLITAIRE_HIGHSCORES_COLLECTION,
    ];
    try {
      await Promise.all([
        ...collections.map(async (collectionName) => {
          const userScoresQuery = query(
            collection(db, collectionName),
            where("userId", "==", userId)
          );
          const snapshot = await getDocs(userScoresQuery);
          if (snapshot.empty) return;
          await Promise.all(
            snapshot.docs.map((docSnap) =>
              setDoc(
                doc(db, collectionName, docSnap.id),
                { displayName: newDisplayName },
                { merge: true }
              )
            )
          );
        }),
        syncOpsTicklesDisplayName(userId, newDisplayName),
      ]);
    } catch (error) {
      console.error("Error updating display names in high scores:", error);
    }
  };

  // Update language preference
  const updateLanguage = async (newLanguage) => {
    setLanguage(newLanguage);
    await updateUserData({ language: newLanguage });
  };

  // Update display name
  const updateDisplayName = async (newDisplayName) => {
    setDisplayName(newDisplayName);
    await updateUserData({ displayName: newDisplayName });
    await syncHighScoresDisplayName(newDisplayName);
  };

  // Save high score for dino game
  const saveHighScore = async (score) => {
    if (!userId || !displayName || !db) return;

    try {
      await addDoc(collection(db, HIGHSCORES_COLLECTION), {
        userId,
        displayName,
        score,
        timestamp: new Date(),
      });
    } catch (error) {
      console.error("Error saving high score:", error);
    }
  };

  // Get top high scores for dino game
  const getHighScores = async (limitCount = 10) => {
    if (!db) return [];

    try {
      const q = query(
        collection(db, HIGHSCORES_COLLECTION),
        orderBy("score", "desc"),
        limit(limitCount)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error getting high scores:", error);
      return [];
    }
  };

  // Save maze result (lower time is better; include moves and level)
  const saveMazeScore = async ({ timeSec, moves, level }) => {
    if (!userId || !displayName || !db) return;
    try {
      await addDoc(collection(db, MAZE_HIGHSCORES_COLLECTION), {
        userId,
        displayName,
        timeSec,
        moves,
        level,
        timestamp: new Date(),
      });
    } catch (error) {
      console.error("Error saving maze score:", error);
    }
  };

  const saveSolitaireScore = async ({ timeSec, moves }) => {
    if (!userId || !displayName || !db) return;
    try {
      await addDoc(collection(db, SOLITAIRE_HIGHSCORES_COLLECTION), {
        userId,
        displayName,
        timeSec,
        moves,
        timestamp: new Date(),
      });
    } catch (error) {
      console.error("Error saving solitaire score:", error);
    }
  };

  const getSolitaireHighScores = async (limitCount = 10) => {
    if (!db) return [];
    try {
      const q = query(
        collection(db, SOLITAIRE_HIGHSCORES_COLLECTION),
        orderBy("timeSec", "asc"),
        limit(limitCount)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((scoreDoc) => ({
        id: scoreDoc.id,
        ...scoreDoc.data(),
      }));
    } catch (error) {
      console.error("Error getting solitaire high scores:", error);
      return [];
    }
  };

  // Get top maze results ordered by fastest time
  const getMazeHighScores = async (limitCount = 10) => {
    if (!db) return [];
    try {
      const q = query(
        collection(db, MAZE_HIGHSCORES_COLLECTION),
        orderBy("timeSec", "asc"),
        limit(limitCount)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error getting maze high scores:", error);
      return [];
    }
  };

  // Submit a reward claim for shipping
  const submitRewardClaim = async ({
    rewardId,
    rewardName,
    color,
    shippingName,
    address,
    cost = 0,
  }) => {
    if (!userId || !db) {
      throw new Error("Firebase is not available");
    }

    const starCost = Math.max(0, Number(cost) || 0);
    if (starCost > 0 && pointsRef.current < starCost) {
      const error = new Error("Insufficient stars");
      error.code = "insufficient-stars";
      throw error;
    }

    const claim = {
      userId,
      displayName: displayName || null,
      rewardId,
      rewardName,
      color,
      shippingName,
      address,
      cost: starCost,
      status: "pending",
      createdAt: new Date(),
    };

    try {
      const docRef = await addDoc(
        collection(db, REWARD_CLAIMS_COLLECTION),
        claim
      );
      if (starCost > 0) {
        await addPoints(-starCost);
      }
      return { id: docRef.id, ...claim };
    } catch (error) {
      console.error("Error submitting reward claim:", error);
      throw error;
    }
  };

  // Always write problem reports to the production inbox so the admin dashboard can see them.
  const submitProblemReport = async (message) => {
    if (!userId || !db) {
      throw new Error("Firebase is not available");
    }

    const trimmed = String(message || "").trim();
    if (!trimmed) {
      const error = new Error("Message is required");
      error.code = "message-required";
      throw error;
    }
    if (trimmed.length > MAX_PROBLEM_REPORT_LENGTH) {
      const error = new Error("Message is too long");
      error.code = "message-too-long";
      throw error;
    }

    const report = {
      userId,
      displayName: displayName || null,
      message: trimmed,
      createdAt: new Date(),
      status: "new",
    };

    try {
      const collectionName =
        PROBLEM_REPORTS_COLLECTION === ADMIN_PROBLEM_REPORTS_COLLECTION
          ? PROBLEM_REPORTS_COLLECTION
          : ADMIN_PROBLEM_REPORTS_COLLECTION;
      const docRef = await addDoc(collection(db, collectionName), report);
      return { id: docRef.id, ...report };
    } catch (error) {
      console.error("Error submitting problem report:", error);
      throw error;
    }
  };

  // List all users for the admin dashboard (always production Firebase).
  const getAllUsers = async () => {
    if (!db) return [];
    try {
      const querySnapshot = await getDocs(
        collection(db, ADMIN_USERS_COLLECTION)
      );
      return querySnapshot.docs.map((userDoc) => {
        const data = userDoc.data();
        const toDate = (value) => {
          if (!value) return null;
          if (typeof value.toDate === "function") return value.toDate();
          if (value instanceof Date) return value;
          const parsed = new Date(value);
          return Number.isNaN(parsed.getTime()) ? null : parsed;
        };

        return {
          userId: userDoc.id,
          points: data.points || 0,
          displayName: data.displayName || null,
          language: data.language || "en",
          createdAt: toDate(data.createdAt),
          lastUpdated: toDate(data.lastUpdated),
        };
      });
    } catch (error) {
      console.error("Error getting all users:", error);
      return [];
    }
  };

  // Admin: set a user's star balance in production Firebase.
  const adminUpdateUserPoints = async (targetUserId, nextPoints) => {
    if (!db || !targetUserId) {
      throw new Error("Firebase is not available");
    }

    const pointsValue = Math.max(0, Math.floor(Number(nextPoints) || 0));

    try {
      await setDoc(
        doc(db, ADMIN_USERS_COLLECTION, targetUserId),
        { points: pointsValue, lastUpdated: new Date() },
        { merge: true }
      );

      if (targetUserId === userId && USERS_COLLECTION === ADMIN_USERS_COLLECTION) {
        pointsRef.current = pointsValue;
        setPoints(pointsValue);
        safeSetItem("arcadePoints", pointsValue.toString());
      }

      return { userId: targetUserId, points: pointsValue };
    } catch (error) {
      console.error("Error updating user points:", error);
      throw error;
    }
  };

  const toDate = (value) => {
    if (!value) return null;
    if (typeof value.toDate === "function") return value.toDate();
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const adminScoreCollectionForGame = (gameId) => {
    if (gameId === "dino") return ADMIN_DINO_HIGHSCORES_COLLECTION;
    if (gameId === "maze") return ADMIN_MAZE_HIGHSCORES_COLLECTION;
    if (gameId === "solitaire") return ADMIN_SOLITAIRE_HIGHSCORES_COLLECTION;
    if (gameId === "opsTickles") return ADMIN_OPS_TICKLES_HIGHSCORES_COLLECTION;
    return null;
  };

  // Admin: list all high scores for a leaderboard (production Firebase).
  const adminGetAllScores = async (gameId) => {
    if (!db) return [];
    const collectionName = adminScoreCollectionForGame(gameId);
    if (!collectionName) return [];

    try {
      const querySnapshot = await getDocs(collection(db, collectionName));
      const rows = querySnapshot.docs.map((scoreDoc) => {
        const data = scoreDoc.data() || {};
        return {
          id: scoreDoc.id,
          gameId,
          userId: data.userId || null,
          displayName: data.displayName || null,
          score: data.score != null ? Number(data.score) : null,
          points: data.points != null ? Number(data.points) : null,
          timeSec: data.timeSec != null ? Number(data.timeSec) : null,
          moves: data.moves != null ? Number(data.moves) : null,
          level: data.level != null ? Number(data.level) : null,
          timestamp: toDate(data.timestamp),
          rawGameId: data.gameId || null,
        };
      });

      if (gameId === "maze" || gameId === "solitaire") {
        return rows.sort(
          (a, b) => (a.timeSec ?? Number.POSITIVE_INFINITY) - (b.timeSec ?? Number.POSITIVE_INFINITY)
        );
      }
      if (gameId === "opsTickles") {
        return rows
          .filter((row) => !row.rawGameId || row.rawGameId === "opsTickles")
          .sort(
            (a, b) =>
              (b.points ?? b.score ?? 0) - (a.points ?? a.score ?? 0)
          );
      }
      return rows.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    } catch (error) {
      console.error(`Error getting ${gameId} scores:`, error);
      throw error;
    }
  };

  // Admin: update a high-score document (production Firebase).
  const adminUpdateScore = async (gameId, scoreId, fields) => {
    if (!db || !scoreId) {
      throw new Error("Firebase is not available");
    }
    const collectionName = adminScoreCollectionForGame(gameId);
    if (!collectionName) {
      throw new Error(`Unknown leaderboard: ${gameId}`);
    }

    const payload = {};
    if (fields.displayName !== undefined) {
      payload.displayName = String(fields.displayName || "").trim() || null;
    }
    if (gameId === "dino" && fields.score !== undefined) {
      payload.score = Math.max(0, Math.floor(Number(fields.score) || 0));
    }
    if (gameId === "opsTickles" && fields.points !== undefined) {
      const pointsValue = Math.max(0, Math.floor(Number(fields.points) || 0));
      payload.points = pointsValue;
      payload.score = pointsValue;
    }
    if (gameId === "maze" || gameId === "solitaire") {
      if (fields.timeSec !== undefined) {
        payload.timeSec = Math.max(0, Number(fields.timeSec) || 0);
      }
      if (fields.moves !== undefined) {
        payload.moves = Math.max(0, Math.floor(Number(fields.moves) || 0));
      }
      if (gameId === "maze" && fields.level !== undefined) {
        payload.level = Math.max(1, Math.floor(Number(fields.level) || 1));
      }
    }

    try {
      await setDoc(doc(db, collectionName, scoreId), payload, { merge: true });
      return { id: scoreId, ...payload };
    } catch (error) {
      console.error(`Error updating ${gameId} score:`, error);
      throw error;
    }
  };

  // Admin: delete a high-score document (production Firebase).
  const adminDeleteScore = async (gameId, scoreId) => {
    if (!db || !scoreId) {
      throw new Error("Firebase is not available");
    }
    const collectionName = adminScoreCollectionForGame(gameId);
    if (!collectionName) {
      throw new Error(`Unknown leaderboard: ${gameId}`);
    }

    try {
      await deleteDoc(doc(db, collectionName, scoreId));
      return { id: scoreId };
    } catch (error) {
      console.error(`Error deleting ${gameId} score:`, error);
      throw error;
    }
  };

  // List all reward claims for the admin dashboard (always production Firebase).
  const getAllRewardClaims = async () => {
    if (!db) return [];
    try {
      const querySnapshot = await getDocs(
        collection(db, ADMIN_REWARD_CLAIMS_COLLECTION)
      );
      return querySnapshot.docs.map((claimDoc) => {
        const data = claimDoc.data();
        return {
          id: claimDoc.id,
          userId: data.userId || null,
          displayName: data.displayName || null,
          rewardId: data.rewardId || null,
          rewardName: data.rewardName || null,
          color: data.color || null,
          shippingName: data.shippingName || null,
          address: data.address || null,
          status: data.status || "pending",
          cost: Number(data.cost) || 0,
          createdAt: toDate(data.createdAt),
        };
      });
    } catch (error) {
      console.error("Error getting reward claims:", error);
      return [];
    }
  };

  // List problem reports for the admin dashboard (always production Firebase).
  const getAllProblemReports = async () => {
    if (!db) return [];
    try {
      const querySnapshot = await getDocs(
        collection(db, ADMIN_PROBLEM_REPORTS_COLLECTION)
      );
      return querySnapshot.docs.map((reportDoc) => {
        const data = reportDoc.data() || {};
        return {
          id: reportDoc.id,
          userId: data.userId || null,
          displayName: data.displayName || null,
          message: data.message || "",
          status: data.status || "new",
          createdAt: toDate(data.createdAt),
        };
      });
    } catch (error) {
      console.error("Error getting problem reports:", error);
      throw error;
    }
  };

  const deleteProblemReport = async (reportId) => {
    if (!db || !reportId) {
      throw new Error("Firebase is not available");
    }

    try {
      await deleteDoc(doc(db, ADMIN_PROBLEM_REPORTS_COLLECTION, reportId));
      return { id: reportId };
    } catch (error) {
      console.error("Error deleting problem report:", error);
      throw error;
    }
  };

  // Update reward claim status (admin — production Firebase).
  const updateRewardClaimStatus = async (claimId, status) => {
    if (!db || !claimId) {
      throw new Error("Firebase is not available");
    }

    try {
      await setDoc(
        doc(db, ADMIN_REWARD_CLAIMS_COLLECTION, claimId),
        { status, lastUpdated: new Date() },
        { merge: true }
      );
      return { id: claimId, status };
    } catch (error) {
      console.error("Error updating reward claim status:", error);
      throw error;
    }
  };

  // Delete a reward claim and refund stars to the owner.
  // Admin dashboard deletes use production collections; in-app user cancels use the active env.
  const deleteRewardClaim = async (claimId, options = {}) => {
    if (!db || !claimId) {
      throw new Error("Firebase is not available");
    }

    const useProductionAdmin = Boolean(options.admin);
    const claimsCollection = useProductionAdmin
      ? ADMIN_REWARD_CLAIMS_COLLECTION
      : REWARD_CLAIMS_COLLECTION;
    const usersCollection = useProductionAdmin
      ? ADMIN_USERS_COLLECTION
      : USERS_COLLECTION;

    const REWARD_COST_FALLBACK = {
      benchy: 200,
      turtle: 400,
      "ninja-star": 200,
      "tic-tac-launcher": 500,
      "tic-tac-launcher-advanced": 700,
      "tic-tac-bullet": 200,
      "tic-tac-box-adapter": 150,
      gyro: 450,
      "gravitrax-rails": 350,
      "flying-blade": 750,
      "flying-blade-propeller": 200,
    };

    try {
      const claimRef = doc(db, claimsCollection, claimId);
      const claimSnap = await getDoc(claimRef);
      if (!claimSnap.exists()) {
        throw new Error("Claim not found");
      }

      const data = claimSnap.data() || {};
      const ownerId = data.userId || null;
      const refund = Math.max(
        0,
        Number(data.cost) || REWARD_COST_FALLBACK[data.rewardId] || 0
      );

      await deleteDoc(claimRef);

      if (ownerId && refund > 0) {
        const userRef = doc(db, usersCollection, ownerId);
        const userSnap = await getDoc(userRef);
        const currentPoints = userSnap.exists()
          ? Number(userSnap.data()?.points) || 0
          : ownerId === userId && usersCollection === USERS_COLLECTION
            ? pointsRef.current
            : 0;
        const nextPoints = currentPoints + refund;

        await setDoc(
          userRef,
          { points: nextPoints, lastUpdated: new Date() },
          { merge: true }
        );

        if (ownerId === userId && usersCollection === USERS_COLLECTION) {
          pointsRef.current = nextPoints;
          setPoints(nextPoints);
          safeSetItem("arcadePoints", nextPoints.toString());
        }
      }

      return { id: claimId, refunded: refund, userId: ownerId };
    } catch (error) {
      console.error("Error deleting reward claim:", error);
      throw error;
    }
  };

  // List reward claims for the current user
  const getUserRewardClaims = async () => {
    if (!db || !userId) return [];
    try {
      const q = query(
        collection(db, REWARD_CLAIMS_COLLECTION),
        where("userId", "==", userId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs
        .map((claimDoc) => {
          const data = claimDoc.data();
          return {
            id: claimDoc.id,
            userId: data.userId || null,
            rewardId: data.rewardId || null,
            rewardName: data.rewardName || null,
            color: data.color || null,
            shippingName: data.shippingName || null,
            address: data.address || null,
            status: data.status || "pending",
            cost: Number(data.cost) || 0,
            createdAt: toDate(data.createdAt),
          };
        })
        .sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bTime - aTime;
        });
    } catch (error) {
      console.error("Error getting user reward claims:", error);
      return [];
    }
  };

  const mapMissionDoc = (missionDoc) => {
    const data = missionDoc.data() || {};
    const empty = createEmptyMission(data.slot || 0);
    return {
      id: missionDoc.id,
      slot: data.slot || 0,
      title: data.title || "",
      text: data.text || "",
      imageUrl: data.imageUrl || "",
      type: data.type || empty.type || MISSION_TYPES.MANUAL_CONTENT,
      rewardPoints:
        data.rewardPoints !== undefined
          ? Number(data.rewardPoints) || 0
          : empty.rewardPoints || 0,
      config: {
        target:
          Number(data.config?.target) ||
          Number(empty.config?.target) ||
          1,
      },
      active: data.active !== false,
      updatedAt: toDate(data.updatedAt),
      createdAt: toDate(data.createdAt),
    };
  };

  // Ensure empty mission slots exist, then return all missions sorted by slot
  const getAllMissions = async () => {
    if (!db) {
      return Array.from({ length: MISSION_SLOT_COUNT }, (_, index) =>
        createEmptyMission(index + 1)
      );
    }

    try {
      const querySnapshot = await getDocs(collection(db, MISSIONS_COLLECTION));
      const byId = new Map(
        querySnapshot.docs.map((missionDoc) => [
          missionDoc.id,
          mapMissionDoc(missionDoc),
        ])
      );

      const missingCreates = [];
      for (let slot = 1; slot <= MISSION_SLOT_COUNT; slot += 1) {
        const id = `mission_${String(slot).padStart(2, "0")}`;
        if (!byId.has(id)) {
          const empty = createEmptyMission(slot);
          const { id: _omit, ...payload } = empty;
          const docPayload = {
            ...payload,
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          missingCreates.push(
            setDoc(doc(db, MISSIONS_COLLECTION, id), docPayload).then(() => {
              byId.set(id, { id, ...docPayload });
            })
          );
        }
      }

      if (missingCreates.length > 0) {
        await Promise.all(missingCreates);
      }

      return Array.from({ length: MISSION_SLOT_COUNT }, (_, index) => {
        const slot = index + 1;
        const id = `mission_${String(slot).padStart(2, "0")}`;
        return byId.get(id) || createEmptyMission(slot);
      });
    } catch (error) {
      console.error("Error getting missions:", error);
      return Array.from({ length: MISSION_SLOT_COUNT }, (_, index) =>
        createEmptyMission(index + 1)
      );
    }
  };

  const fileToCompressedDataUrl = (file, maxSize = 900) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Could not read image file"));
      reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error("Could not load image"));
        img.onload = () => {
          const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
          const width = Math.max(1, Math.round(img.width * scale));
          const height = Math.max(1, Math.round(img.height * scale));
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", 0.72));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });

  // Upload a mission image to Storage, falling back to a compressed data URL
  const uploadMissionImage = async (missionId, file, onProgress) => {
    if (!file) throw new Error("No image file provided");

    const reportProgress = (percent) => {
      if (typeof onProgress === "function") {
        onProgress(Math.max(0, Math.min(100, Math.round(percent))));
      }
    };

    if (storage) {
      try {
        const extension = (file.name.split(".").pop() || "jpg").toLowerCase();
        const objectRef = ref(
          storage,
          `missions/${missionId}/${Date.now()}.${extension}`
        );
        const uploadTask = uploadBytesResumable(objectRef, file, {
          contentType: file.type || "image/jpeg",
        });

        const downloadUrl = await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              if (!snapshot.totalBytes) return;
              reportProgress(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100
              );
            },
            (error) => reject(error),
            async () => {
              try {
                reportProgress(100);
                resolve(await getDownloadURL(uploadTask.snapshot.ref));
              } catch (error) {
                reject(error);
              }
            }
          );
        });

        return downloadUrl;
      } catch (error) {
        console.warn(
          "Storage upload failed, falling back to embedded image:",
          error
        );
      }
    }

    reportProgress(30);
    const dataUrl = await fileToCompressedDataUrl(file);
    reportProgress(90);
    if (dataUrl.length > 900000) {
      throw new Error(
        "Image is too large. Try a smaller picture or paste an image URL."
      );
    }
    reportProgress(100);
    return dataUrl;
  };

  // Update mission text / image / rule fields
  const updateMission = async (missionId, updates) => {
    if (!db || !missionId) {
      throw new Error("Firebase is not available");
    }

    const payload = {
      title: updates.title ?? "",
      text: updates.text ?? "",
      imageUrl: updates.imageUrl ?? "",
      type: updates.type || MISSION_TYPES.MANUAL_CONTENT,
      rewardPoints: Number(updates.rewardPoints) || 0,
      config: {
        target: Number(updates.config?.target) || 1,
      },
      active: updates.active !== false,
      updatedAt: new Date(),
    };

    try {
      await setDoc(doc(db, MISSIONS_COLLECTION, missionId), payload, {
        merge: true,
      });
      return { id: missionId, ...payload };
    } catch (error) {
      console.error("Error updating mission:", error);
      throw error;
    }
  };

  const claimCompletedMissions = async (nextProgress, nextMissionProgress) => {
    const missions = await getAllMissions();
    const completable = findCompletableMissions(
      missions,
      nextProgress,
      nextMissionProgress
    );
    if (completable.length === 0) {
      return { missionProgress: nextMissionProgress, claimed: [] };
    }

    let pointsDelta = 0;
    const claimed = [];
    const updatedMissionProgress = { ...nextMissionProgress };

    completable.forEach(({ mission, view }) => {
      const reward = Number(mission.rewardPoints) || 0;
      if (reward <= 0) return;
      pointsDelta += reward;
      updatedMissionProgress[mission.id] = {
        status: "claimed",
        progress: view.target,
        target: view.target,
        completedAt: new Date().toISOString(),
        rewardPoints: reward,
      };
      claimed.push({
        missionId: mission.id,
        title: mission.title || mission.id,
        rewardPoints: reward,
      });
    });

    if (pointsDelta > 0) {
      const newPoints = Math.max(0, pointsRef.current + pointsDelta);
      pointsRef.current = newPoints;
      setPoints(newPoints);
      await updateUserData({ points: newPoints });
      setRecentMissionClaims(claimed);
    }

    return { missionProgress: updatedMissionProgress, claimed };
  };

  const recordGameEvent = async (type, payload = {}) => {
    if (!userId) return { gameProgress: gameProgressRef.current, claimed: [] };

    const nextProgress = applyGameEvent(
      gameProgressRef.current,
      type,
      payload
    );
    gameProgressRef.current = nextProgress;
    setGameProgress(nextProgress);
    safeSetItem("arcadeGameProgress", JSON.stringify(nextProgress));

    let nextMissionProgress = { ...missionProgressRef.current };
    const claimResult = await claimCompletedMissions(
      nextProgress,
      nextMissionProgress
    );
    nextMissionProgress = claimResult.missionProgress;
    missionProgressRef.current = nextMissionProgress;
    setMissionProgress(nextMissionProgress);
    safeSetItem("arcadeMissionProgress", JSON.stringify(nextMissionProgress));

    await updateUserData({
      gameProgress: nextProgress,
      missionProgress: nextMissionProgress,
    });

    return {
      gameProgress: nextProgress,
      missionProgress: nextMissionProgress,
      claimed: claimResult.claimed,
    };
  };

  const clearRecentMissionClaims = () => setRecentMissionClaims([]);

  const markEnglishLessonComplete = async (lessonId, { sessionPoints = 0 } = {}) => {
    if (!lessonId) return englishProgressRef.current;

    const previous = englishProgressRef.current || {
      learnerName: "",
      lessons: {},
    };
    const existing = previous.lessons?.[lessonId];
    const next = {
      learnerName: previous.learnerName || "",
      lessons: {
        ...(previous.lessons || {}),
        [lessonId]: {
          done: true,
          completedAt: existing?.completedAt || new Date().toISOString(),
          sessionPoints:
            typeof sessionPoints === "number"
              ? sessionPoints
              : existing?.sessionPoints || 0,
          timesCompleted: (existing?.timesCompleted || 0) + 1,
        },
      },
    };

    englishProgressRef.current = next;
    setEnglishProgress(next);
    safeSetItem("arcadeEnglishProgress", JSON.stringify(next));
    await updateUserData({ englishProgress: next });
    return next;
  };

  const saveEnglishLearnerName = async (rawName) => {
    const name = String(rawName || "").trim();
    if (!name) return { ok: false, reason: "empty" };

    const previous = englishProgressRef.current || {
      learnerName: "",
      lessons: {},
    };
    // Name can only be set once.
    if (previous.learnerName) {
      return { ok: false, reason: "already_set", name: previous.learnerName };
    }

    const next = {
      learnerName: name,
      lessons: { ...(previous.lessons || {}) },
    };
    englishProgressRef.current = next;
    setEnglishProgress(next);
    safeSetItem("arcadeEnglishProgress", JSON.stringify(next));
    await updateUserData({ englishProgress: next });
    return { ok: true, name };
  };

  const isEnglishLessonDone = (lessonId) =>
    Boolean(englishProgressRef.current?.lessons?.[lessonId]?.done);

  const savePixelAvatar = async (nextAvatar) => {
    const normalized = parseStoredPixelAvatar(nextAvatar);
    if (!normalized) return null;
    setPixelAvatar(normalized);
    await updateUserData({ pixelAvatar: normalized });
    return normalized;
  };

  const getMissionStatus = (mission) =>
    getMissionProgressView(
      mission,
      gameProgressRef.current,
      missionProgressRef.current[mission?.id]
    );

  const value = {
    userId,
    points,
    displayName,
    language,
    loading,
    gameProgress,
    missionProgress,
    englishProgress,
    pixelAvatar,
    recentMissionClaims,
    addPoints,
    setPoints: setPointsDirect,
    updateLanguage,
    updateDisplayName,
    saveHighScore,
    getHighScores,
    saveMazeScore,
    getMazeHighScores,
    saveSolitaireScore,
    getSolitaireHighScores,
    submitRewardClaim,
    submitProblemReport,
    getAllUsers,
    adminUpdateUserPoints,
    adminGetAllScores,
    adminUpdateScore,
    adminDeleteScore,
    getAllRewardClaims,
    getAllProblemReports,
    deleteProblemReport,
    updateRewardClaimStatus,
    deleteRewardClaim,
    getUserRewardClaims,
    getAllMissions,
    updateMission,
    uploadMissionImage,
    recordGameEvent,
    clearRecentMissionClaims,
    getMissionStatus,
    markEnglishLessonComplete,
    saveEnglishLearnerName,
    isEnglishLessonDone,
    savePixelAvatar,
  };

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
};
